import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DeclarationsAuthService } from '../auth/declarations-auth.service';
import { environment } from 'src/environments/environment';
import { UsersService } from '../core/users.service';
import {
  ActionButton,
  computeActionButtons,
  computeUserMenuItems,
  NavigationItem,
  UserMenuItem
} from './navigation.config';
import { ThemeService } from '../core/theme.service';
import { AvatarCardUser } from '../core/basic-user.model';

@Component({
  selector: 'ad-nav-menu',
  imports: [RouterLink],
  template: `
    @if (loggedIn) {
      <nav class="navbar navbar-expand-lg bg-body-tertiary border-bottom">
        <div class="container-fluid px-lg-4">
          <!-- Mobile Burger Menu (Left Side) -->
          <button
            class="btn btn-link nav-link p-2 d-lg-none me-2"
            type="button"
            (click)="toggleDrawer()"
            aria-label="Menu"
          >
            <span class="material-icons">menu</span>
          </button>

          <!-- Logo and Brand -->
          <a class="navbar-brand d-flex align-items-center" [routerLink]="['/']">
            <img src="assets/cigp-logo.png" style="width: 32px; height: 32px;" alt="Logo" class="me-2" />
            <span class="">{{ appName }}</span>
          </a>

          <!-- Desktop Left Menu Items -->
          <ul class="navbar-nav me-auto d-none d-lg-flex">
            @for (item of navigationItems; track item.route) {
              @if (item.children && item.children.length > 0) {
                <li class="nav-item dropdown">
                  <a
                    class="nav-link dropdown-toggle"
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    {{ item.label }}
                  </a>
                  <ul class="dropdown-menu">
                    @for (child of item.children; track child.route) {
                      <li>
                        <a class="dropdown-item" [routerLink]="[child.route]">{{ child.label }}</a>
                      </li>
                    }
                  </ul>
                </li>
              } @else {
                <!-- Regular Menu Item -->
                <li class="nav-item">
                  <a class="nav-link" [routerLink]="[item.route]" routerLinkActive="active">{{ item.label }}</a>
                </li>
              }
            }
          </ul>

          <!-- Desktop Right Icons and Avatar -->
          <ul class="navbar-nav align-items-center gap-1 d-none d-lg-flex">
            @for (action of actionButtons; track action.id) {
              @if (action.id === 'theme-toggle') {
                <!-- Theme Toggle -->
                <li class="nav-item">
                  <button
                    class="btn btn-link nav-link p-2"
                    type="button"
                    [attr.aria-label]="action.label"
                    (click)="toggleTheme()"
                  >
                    @if (isDarkMode) {
                      <span class="material-icons">dark_mode</span>
                    } @else {
                      <span class="material-icons">light_mode</span>
                    }
                  </button>
                </li>
              }
            }

            <!-- Divider -->
            <li class="nav-item">
              <div class="vr mx-2" style="height: 24px;"></div>
            </li>

            <!-- User Dropdown -->
            <li class="nav-item dropdown">
              <a
                class="nav-link p-1"
                href="#"
                id="userDropdown"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                @if (me) {
                  @if (me.profilePicture) {
                    <span class="e-avatar e-avatar-circle e-avatar avatar-shell">
                      <img
                        [src]="me.profilePicture"
                        [alt]="me.name"
                        [title]="me.name"
                        class="avatar-img"
                        width="32"
                        height="32"
                      />
                    </span>
                  } @else {
                    <span
                      class="e-avatar e-avatar-circle e-avatar text-white d-inline-flex justify-content-center align-items-center avatar-shell"
                      [title]="me.name"
                    >
                      {{ me.initials }}
                    </span>
                  }
                }
              </a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                <li>
                  <h6 class="dropdown-header text-truncate" style="max-width: 250px;">
                    {{ me?.name || 'User' }}
                  </h6>
                </li>
                <li>
                  <div class="px-3 py-1 text-muted small text-truncate" style="max-width: 250px;">
                    {{ userEmail || 'Not logged in' }}
                  </div>
                </li>
                <li><hr class="dropdown-divider" /></li>

                @for (item of userMenuItems; track item.id) {
                  @if (item.action === 'theme-toggle') {
                    <!-- Theme Toggle -->
                    <li>
                      <a class="dropdown-item" href="#" (click)="toggleTheme(); $event.preventDefault()">
                        @if (isDarkMode) {
                          <span class="material-icons me-2">light_mode</span> Switch to Light Mode
                        } @else {
                          <span class="material-icons me-2">dark_mode</span> Switch to Dark Mode
                        }
                      </a>
                    </li>
                  } @else if (item.action === 'logout') {
                    <!-- Logout -->
                    <li>
                      <a class="dropdown-item" href="#" (click)="logout($event)">
                        <span class="material-icons me-2">{{ item.icon }}</span> {{ item.label }}
                      </a>
                    </li>
                  } @else if (item.url) {
                    <!-- External Link (Feedback, Manual, etc.) -->
                    <li>
                      <a class="dropdown-item" [href]="item.url" target="_blank">
                        <span class="material-icons me-2">{{ item.icon }}</span> {{ item.label }}
                      </a>
                    </li>
                  } @else {
                    <!-- Regular Menu Item -->
                    <li>
                      <a class="dropdown-item" href="#" (click)="$event.preventDefault()">
                        <span class="material-icons me-2">{{ item.icon }}</span> {{ item.label }}
                      </a>
                    </li>
                  }
                  @if (item.dividerAfter) {
                    <li><hr class="dropdown-divider" /></li>
                  }
                }
              </ul>
            </li>
          </ul>

          <!-- Mobile User Dropdown (Right Side) -->
          <div class="d-lg-none ms-auto">
            <div class="dropdown">
              <a
                class="nav-link p-1"
                href="#"
                id="userDropdownMobile"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                @if (me) {
                  @if (me.profilePicture) {
                    <span class="e-avatar e-avatar-circle e-avatar avatar-shell">
                      <img
                        [src]="me.profilePicture"
                        [alt]="me.name"
                        [title]="me.name"
                        class="avatar-img"
                        width="32"
                        height="32"
                      />
                    </span>
                  } @else {
                    <span
                      class="e-avatar e-avatar-circle e-avatar text-white d-inline-flex justify-content-center align-items-center avatar-default-bg avatar-shell"
                      [title]="me.name"
                    >
                      {{ me.initials }}
                    </span>
                  }
                }
              </a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdownMobile">
                <li>
                  <h6 class="dropdown-header text-truncate" style="max-width: 250px;">
                    {{ me?.name || 'User' }}
                  </h6>
                </li>
                <li>
                  <div class="px-3 py-1 text-muted small text-truncate" style="max-width: 250px;">
                    {{ userEmail || 'Not logged in' }}
                  </div>
                </li>
                <li><hr class="dropdown-divider" /></li>

                <!-- Mobile-specific items (Search, Notifications) -->
                @for (mobileItem of mobileUserMenuItems; track mobileItem.id) {
                  <li>
                    <a class="dropdown-item" href="#" (click)="$event.preventDefault()">
                      <span class="material-icons me-2">{{ mobileItem.icon }}</span>
                      {{ mobileItem.label }}
                    </a>
                  </li>
                }

                <li><hr class="dropdown-divider" /></li>

                <!-- Regular user menu items -->
                @for (item of userMenuItems; track item.id) {
                  @if (item.action === 'theme-toggle') {
                    <!-- Theme Toggle -->
                    <li>
                      <a class="dropdown-item" href="#" (click)="toggleTheme(); $event.preventDefault()">
                        @if (isDarkMode) {
                          <span class="material-icons me-2">light_mode</span> Switch to Light Mode
                        } @else {
                          <span class="material-icons me-2">dark_mode</span> Switch to Dark Mode
                        }
                      </a>
                    </li>
                  } @else if (item.action === 'logout') {
                    <!-- Logout -->
                    <li>
                      <a class="dropdown-item" href="#" (click)="logout($event)">
                        <span class="material-icons me-2">{{ item.icon }}</span> {{ item.label }}
                      </a>
                    </li>
                  } @else if (item.url) {
                    <!-- External Link -->
                    <li>
                      <a class="dropdown-item" [href]="item.url" target="_blank">
                        <span class="material-icons me-2">{{ item.icon }}</span> {{ item.label }}
                      </a>
                    </li>
                  } @else {
                    <!-- Regular Menu Item -->
                    <li>
                      <a class="dropdown-item" href="#" (click)="$event.preventDefault()">
                        <span class="material-icons me-2">{{ item.icon }}</span> {{ item.label }}
                      </a>
                    </li>
                  }
                  @if (item.dividerAfter) {
                    <li><hr class="dropdown-divider" /></li>
                  }
                }
              </ul>
            </div>
          </div>
        </div>
      </nav>

      <!-- Mobile Drawer Overlay -->
      @if (drawerOpen) {
        <div class="drawer-overlay" (click)="closeDrawer()"></div>
      }

      <!-- Mobile Left Drawer -->
      <div class="drawer" [class.drawer-open]="drawerOpen">
        <div class="drawer-header">
          <h5 class="m-0">Menu</h5>
          <button class="btn btn-link p-0" (click)="closeDrawer()" aria-label="Close menu">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="drawer-body">
          <ul class="nav flex-column">
            @for (item of navigationItems; track item.route) {
              @if (item.children && item.children.length > 0) {
                <!-- Parent Item (Not Clickable) -->
                <li class="nav-item">
                  <div class="nav-link text-muted fw-semibold">
                    @if (item.icon) {
                      <span class="material-icons me-2">{{ item.icon }}</span>
                    }
                    {{ item.label }}
                  </div>
                </li>
                <!-- Child Items (Indented) -->
                @for (child of item.children; track child.route) {
                  <li class="nav-item">
                    <a
                      class="nav-link ps-5"
                      [routerLink]="[child.route]"
                      routerLinkActive="active"
                      (click)="closeDrawer()"
                    >
                      @if (child.icon) {
                        <span class="material-icons me-2">{{ child.icon }}</span>
                      }
                      {{ child.label }}
                    </a>
                  </li>
                }
              } @else {
                <!-- Regular Item -->
                <li class="nav-item">
                  <a class="nav-link" [routerLink]="[item.route]" routerLinkActive="active" (click)="closeDrawer()">
                    @if (item.icon) {
                      <span class="material-icons me-2">{{ item.icon }}</span>
                    }
                    {{ item.label }}
                  </a>
                </li>
              }
            }
          </ul>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .avatar-shell {
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        overflow: hidden;
      }

      .avatar-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .dropdown-menu {
        min-width: auto;
        width: max-content;
        white-space: nowrap;
      }

      .dropdown-item {
        white-space: nowrap;
      }

      .nav-item.dropdown .nav-link.dropdown-toggle.show {
        color: inherit;
      }
    `
  ]
})
export class NavMenuComponent implements OnInit {
  protected me: AvatarCardUser | null = null;
  protected environment = environment.env;
  protected appName = environment.appName;
  protected loggedIn = false;
  protected userEmail: string | null = null;
  protected isDarkMode = false;
  protected drawerOpen = false;

  // Navigation configuration
  protected navigationItems: NavigationItem[] = [];
  protected actionButtons: ActionButton[] = [];
  protected userMenuItems: UserMenuItem[] = [];
  protected mobileUserMenuItems: UserMenuItem[] = [];

  constructor(
    private declarationsAuthService: DeclarationsAuthService,
    private usersService: UsersService,
    private themeService: ThemeService
  ) {}

  public ngOnInit(): void {
    this.declarationsAuthService.loggedIn$.subscribe((loggedIn) => {
      this.loggedIn = loggedIn;
      if (loggedIn) {
        this.usersService.getMyAvatarCardInfo$().subscribe((me) => {
          this.me = me;
          this.userEmail = me ? me.email : null;
        });

        this.actionButtons = computeActionButtons();
        this.userMenuItems = computeUserMenuItems(environment);
      }
    });
  }

  public toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  public toggleDrawer(): void {
    this.drawerOpen = !this.drawerOpen;
    // Prevent body scroll when drawer is open
    if (this.drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  public closeDrawer(): void {
    this.drawerOpen = false;
    document.body.style.overflow = '';
  }

  public logout(event: Event): void {
    event.preventDefault();
    this.declarationsAuthService.logout$().subscribe();
  }
}
